import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/components/ui/use-toast"
import { LANGUAGES, type Language } from "@/lib/constants"
import { LoadingDialog } from "@/components/ui/loading-dialog"
import { Copy, Check, Users, Clock, Target, Sparkles } from "lucide-react"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"

interface EmailSettings {
  tone: string
  length: string
  language: Language["value"]
  includeCallToAction: boolean
}

interface ProjectContext {
  description: string
  startDate: string
  duration: string
  location: string
}

interface EmailResponse {
  subject: string
  body: string
  signature: string
  imageSuggestions: string[]
}

const volunteerTypes = [
  {
    id: "event_volunteer",
    name: "Event Volunteer",
    description: "Help with organizing and running events",
    icon: <Clock className="h-5 w-5 text-blue-500" />
  },
  {
    id: "skilled_volunteer",
    name: "Skilled Volunteer",
    description: "Professional skills like design, tech, marketing",
    icon: <Sparkles className="h-5 w-5 text-purple-500" />
  },
  {
    id: "regular_volunteer",
    name: "Regular Volunteer",
    description: "Ongoing weekly/monthly commitment",
    icon: <Users className="h-5 w-5 text-green-500" />
  },
  {
    id: "project_volunteer",
    name: "Project Volunteer",
    description: "Specific project or initiative based",
    icon: <Target className="h-5 w-5 text-orange-500" />
  }
]

export default function VolunteerRecruitmentEmailPage({ profile, session }: { profile: any | null, session: any | null }) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [generatedEmail, setGeneratedEmail] = useState<EmailResponse | null>(null)
  const [copied, setCopied] = useState(false)
  
  const [settings, setSettings] = useState<EmailSettings>({
    tone: "friendly",
    length: "medium",
    language: "en",
    includeCallToAction: true
  })

  const [projectContext, setProjectContext] = useState<ProjectContext>({
    description: "",
    startDate: "",
    duration: "",
    location: ""
  })

  const [selectedVolunteerType, setSelectedVolunteerType] = useState("")
  const [urgencyLevel, setUrgencyLevel] = useState("medium")
  const [timeCommitment, setTimeCommitment] = useState("flexible")

  const handleCopy = () => {
    if (!generatedEmail) return
    const fullEmail = `${generatedEmail.subject}\n\n${generatedEmail.body}${generatedEmail.signature}`
    navigator.clipboard.writeText(fullEmail)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast({
      title: "Copied to clipboard",
      description: "Email content has been copied to your clipboard"
    })
  }

  const generateEmail = async () => {
    if (!selectedVolunteerType) {
      toast({
        title: "Please select a volunteer type",
        description: "Choose the type of volunteers you're looking for",
        variant: "destructive"
      })
      return
    }

    if (!projectContext.description) {
      toast({
        title: "Project description required",
        description: "Please provide some details about the project",
        variant: "destructive"
      })
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          type: selectedVolunteerType,
          context: {
            ...projectContext,
            urgencyLevel,
            timeCommitment
          },
          settings: {
            tone: settings.tone || 'friendly',
            length: settings.length || 'medium',
            format: 'plain',
            language: settings.language || 'en',
            includeCallToAction: settings.includeCallToAction,
            includeImages: true
          }
        })
      })

      if (!response.ok) {
        throw new Error('Failed to generate email')
      }

      const data = await response.json()
      setGeneratedEmail(data.data)
    } catch (error) {
      toast({
        title: "Error generating email",
        description: "Please try again later",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Volunteer Recruitment Email</h2>
          <p className="text-muted-foreground">
            Create engaging emails to attract volunteers to your cause
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">What type of volunteers are you looking for?</h3>
            <div className="grid grid-cols-2 gap-4">
              {volunteerTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedVolunteerType(type.id)}
                  className={`p-4 rounded-lg border text-left transition-colors hover:bg-gray-50 ${
                    selectedVolunteerType === type.id ? "border-primary ring-2 ring-primary/20" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {type.icon}
                    <div>
                      <div className="font-medium">{type.name}</div>
                      <div className="text-sm text-muted-foreground">{type.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">How urgent is your need?</h3>
            <RadioGroup
              value={urgencyLevel}
              onValueChange={setUrgencyLevel}
              className="grid grid-cols-3 gap-4"
            >
              <div>
                <RadioGroupItem value="low" id="low" className="peer sr-only" />
                <Label
                  htmlFor="low"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-gray-50 peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <span>Low</span>
                </Label>
              </div>
              <div>
                <RadioGroupItem value="medium" id="medium" className="peer sr-only" />
                <Label
                  htmlFor="medium"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-gray-50 peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <span>Medium</span>
                </Label>
              </div>
              <div>
                <RadioGroupItem value="high" id="high" className="peer sr-only" />
                <Label
                  htmlFor="high"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-gray-50 peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <span>High</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Time Commitment</h3>
            <RadioGroup
              value={timeCommitment}
              onValueChange={setTimeCommitment}
              className="grid grid-cols-3 gap-4"
            >
              <div>
                <RadioGroupItem value="flexible" id="flexible" className="peer sr-only" />
                <Label
                  htmlFor="flexible"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-gray-50 peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <span>Flexible</span>
                </Label>
              </div>
              <div>
                <RadioGroupItem value="part_time" id="part_time" className="peer sr-only" />
                <Label
                  htmlFor="part_time"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-gray-50 peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <span>Part-time</span>
                </Label>
              </div>
              <div>
                <RadioGroupItem value="full_time" id="full_time" className="peer sr-only" />
                <Label
                  htmlFor="full_time"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-gray-50 peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <span>Full-time</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Project Details</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="description">Project Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of the project or initiative"
                  value={projectContext.description}
                  onChange={(e) => setProjectContext({ ...projectContext, description: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    type="date"
                    id="startDate"
                    value={projectContext.startDate}
                    onChange={(e) => setProjectContext({ ...projectContext, startDate: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    type="text"
                    id="duration"
                    placeholder="e.g., 2 weeks, 3 months"
                    value={projectContext.duration}
                    onChange={(e) => setProjectContext({ ...projectContext, duration: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  type="text"
                  id="location"
                  placeholder="Where will the volunteering take place?"
                  value={projectContext.location}
                  onChange={(e) => setProjectContext({ ...projectContext, location: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Email Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="tone">Tone</Label>
                <Select
                  value={settings.tone}
                  onValueChange={(value) => setSettings({ ...settings, tone: value })}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select tone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="enthusiastic">Enthusiastic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="language">Language</Label>
                <Select
                  value={settings.language}
                  onValueChange={(value) => setSettings({ ...settings, language: value })}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="call-to-action"
                  checked={settings.includeCallToAction}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, includeCallToAction: checked })
                  }
                />
                <Label htmlFor="call-to-action">Include clear call-to-action buttons</Label>
              </div>
            </div>
          </div>

          <Button
            onClick={generateEmail}
            className="w-full"
            size="lg"
          >
            Generate Recruitment Email
          </Button>
        </Card>

        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Generated Email</h3>
              {generatedEmail && (
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
            <div className="min-h-[300px] rounded-lg border bg-muted/40 p-4">
              {generatedEmail ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Subject</h4>
                    <p className="mt-1">{generatedEmail.subject}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Body</h4>
                    <div className="mt-1 whitespace-pre-wrap">
                      {generatedEmail.body}
                      {generatedEmail.signature}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  Your generated email will appear here
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      <LoadingDialog
        open={loading}
        title="Generating Email"
        description="Our AI is crafting the perfect volunteer recruitment email..."
      />
    </div>
  )
}
